/**
 * PersonaCard Component
 *
 * Styled card displaying persona information:
 * - Name and role
 * - Communication style as tags
 * - Emotional state indicator
 * - Background as narrative paragraph
 */

import type { PersonaDefinition } from '../lib/types';

interface PersonaCardProps {
  persona: PersonaDefinition;
}

const EMOTION_INDICATORS: Record<string, { color: string; bg: string }> = {
  neutral: { color: 'var(--slate-600)', bg: 'var(--slate-100)' },
  calm: { color: 'var(--sky-600)', bg: 'var(--sky-50)' },
  frustrated: { color: 'var(--amber-700)', bg: 'var(--amber-50)' },
  anxious: { color: 'var(--amber-600)', bg: 'var(--amber-50)' },
  angry: { color: 'var(--red-700)', bg: 'var(--red-50)' },
  upset: { color: 'var(--red-600)', bg: 'var(--red-50)' },
  happy: { color: 'var(--emerald-700)', bg: 'var(--emerald-50)' },
  confident: { color: 'var(--emerald-600)', bg: 'var(--emerald-50)' },
  stressed: { color: 'var(--amber-700)', bg: 'var(--amber-50)' },
};

function getEmotionStyle(emotionalState: string): { color: string; bg: string } {
  const lower = emotionalState.toLowerCase();
  for (const [key, style] of Object.entries(EMOTION_INDICATORS)) {
    if (lower.includes(key)) return style;
  }
  return EMOTION_INDICATORS['neutral']!;
}

function parseStyleTags(communicationStyle: string): string[] {
  // Split on commas, semicolons, or " and " to extract individual style tags
  return communicationStyle
    .split(/[,;]|\band\b/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function PersonaCard({ persona }: PersonaCardProps) {
  const emotionStyle = getEmotionStyle(persona.emotionalState);
  const cs = persona.communicationStyle;
  const styleStr = typeof cs === 'object' && cs !== null
    ? `${cs.tone}, ${cs.formality}, ${cs.pace}, ${cs.verbosity}`
    : String(cs);
  const styleTags = parseStyleTags(styleStr);

  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          background: 'var(--slate-800)',
          color: 'white',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 2 }}>{persona.name}</div>
          <div style={{ fontSize: 13, color: 'var(--slate-300)' }}>{persona.role}</div>
        </div>
        {/* Emotional state badge */}
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            padding: '4px 10px',
            borderRadius: 'var(--radius-full)',
            background: emotionStyle.bg,
            color: emotionStyle.color,
            textTransform: 'capitalize',
          }}
        >
          {persona.emotionalState}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px' }}>
        {/* Communication style tags */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-secondary)',
              marginBottom: 6,
            }}
          >
            Communication Style
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {styleTags.map((tag, i) => (
              <span
                key={i}
                style={{
                  fontSize: 12,
                  padding: '3px 10px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--sky-50)',
                  color: 'var(--sky-600)',
                  border: '1px solid var(--sky-200)',
                  fontWeight: 500,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Background */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-secondary)',
              marginBottom: 6,
            }}
          >
            Background
          </div>
          <p
            style={{
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--text-primary)',
              margin: 0,
            }}
          >
            {persona.background}
          </p>
        </div>

        {/* Objectives */}
        {persona.objectives.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-secondary)',
                marginBottom: 6,
              }}
            >
              Objectives
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 13,
                color: 'var(--text-primary)',
                lineHeight: 1.6,
              }}
            >
              {persona.objectives.map((obj, i) => (
                <li key={i}>{obj}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Constraints */}
        {persona.constraints.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-secondary)',
                marginBottom: 6,
              }}
            >
              Constraints
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 13,
                color: 'var(--text-secondary)',
                lineHeight: 1.6,
              }}
            >
              {persona.constraints.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
